import { Link, useParams } from "react-router-dom";
import { Clock, MessageSquare, FileQuestion } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/common/EmptyState";
import { AgentCard, WorkflowCard } from "@/components/catalogue/Cards";
import { useCatalogue } from "@/context/CatalogueContext";
import { agentByWorkingName, agentName } from "@/lib/catalogue";
import { teamColour } from "@/lib/constants";

export const NotFoundEntry = ({ label, testid }) => (
  <EmptyState icon={FileQuestion} title={`We could not find that ${label}`} body="It may have been renamed or removed from the catalogue." action={<Link to="/agents" className="text-sm font-medium underline underline-offset-4">Find an agent</Link>} testid={testid} />
);

export default function TeamPage() {
  const { teamId } = useParams();
  const { teamsById, agents, workflows, loading } = useCatalogue();
  const team = teamsById[teamId];
  if (loading) return <Skeleton className="h-64 rounded-xl" />;
  if (!team) return <NotFoundEntry label="team" testid="team-not-found" />;
  const teamAgents = agents.filter((a) => a.team === team.id && !a.parent).sort((x, y) => (x.display_order ?? 0) - (y.display_order ?? 0));
  const teamFlows = workflows.filter((w) => w.team === team.id);
  const muted = !teamAgents.some((a) => a.live) && !teamFlows.some((w) => w.live);
  const lead = agentByWorkingName(team.lead, agents);
  return (
    <div data-testid="team-page">
      <div className="mb-8 flex flex-col gap-4 border-l-4 pl-5 md:flex-row md:items-end md:justify-between" style={{ borderColor: teamColour(team, team._index) }}>
        <div className="max-w-3xl">
          <p className="text-sm text-muted-foreground">Team {team.id}</p>
          <h1 className="mt-1 text-xl font-semibold" data-testid="team-title">{team.section || team.name}</h1>
          {team.purpose && <p className="mt-2 text-sm text-muted-foreground md:text-base" data-testid="team-purpose">{team.purpose}</p>}
          {team.lead && <p className="mt-2 text-sm">Led by {lead ? <Link to={`/agents/${lead.emp_no}`} className="font-medium underline underline-offset-4" data-testid="team-lead-link">{agentName(lead)}</Link> : <span className="font-medium">{team.lead}</span>}</p>}
        </div>
        <Button variant="secondary" asChild><Link to={`/feedback?about=${team.id}`} data-testid="team-ask-button"><MessageSquare className="!h-4 !w-4" /> Ask about this team</Link></Button>
      </div>
      {muted && (
        <div className="mb-6 flex items-center gap-3 rounded-xl bg-muted px-4 py-3 text-sm" data-testid="team-coming-soon">
          <Clock className="!h-4 !w-4 shrink-0" aria-hidden="true" />
          <span><span className="font-medium">Coming soon, number {team.order} in the build order.</span> You can read about its agents now.</span>
        </div>
      )}
      <h2 className="mb-4 text-lg font-semibold">Agents</h2>
      {teamAgents.length === 0 ? <p className="text-sm text-muted-foreground">No agents imported for this team yet.</p> : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="team-agent-grid">{teamAgents.map((a) => <AgentCard key={a.emp_no} agent={a} />)}</div>
      )}
      {teamFlows.length > 0 && (
        <section className="mt-12">
          <h2 className="mb-4 text-lg font-semibold">Workflows</h2>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{teamFlows.map((w) => <WorkflowCard key={w.id} workflow={w} />)}</div>
        </section>
      )}
    </div>
  );
}
